import React, { useEffect, useState, useRef, useContext } from 'react';
import { socket } from '../../context/socket';
import Message from '../message/Message';
import ChatBoxBottom from '../chatBoxBottom/ChatBoxBottom';
import { useConfettiText } from '../../hooks/useConfettiText';
import useMobileView from '../../hooks/useMobileView';
import useClickOutside from '../../clickOutside';
import { handleEmoji } from '../../utils/handleEmojis';
import { useIsTyping } from 'use-is-typing';
import { useReactMediaRecorder } from 'react-media-recorder';
import { AuthContext } from '../../context/AuthContext';
import axiosConn from '../../axiosConn';
import ChatBoxMiddle from '../../components/chatBoxMiddle/ChatBoxMiddle';
import { ViewPortContext } from '../../context/ViewPortContext';
import handleFileInput from '../../utils/handleFileInput';
import { debounce } from '../../context/ViewPortContext';
import WelcomeMessage from '../../components/welcomeMessage/WelcomeMessage';
import AdRotator from '../adRotator/AdRotator';
import { v4 as uuidv4 } from 'uuid';
import { MAX_VIDEO_SIZE, MAX_IMAGE_SIZE } from '../../config/constants';
import { INCOMING_MESSAGE_BL_URL } from '../../utils/constants';
import './galaxyChatBox.css';

// NEW (Better-than-FAANG+ uploader)
import {
  createMediaUploader,
  DEFAULT_VIDEO_MIME_TYPES,
  MediaUploadError,
} from '../../utils/mediaUpload';

const MESSAGE_PARTICLE_COUNT = 100;
const maxVideoSize = MAX_VIDEO_SIZE;
const maxImageSize = MAX_IMAGE_SIZE;

const getStableId = (value) => {
  if (!value) return '';
  return String(value._id || value.id || value);
};

const listIncludesPlayer = (list = [], playerId) =>
  list.some((item) => getStableId(item) === String(playerId));

const GalaxyChatBox = ({
  galaxyId,
  senderId,
  senderProfilePhoto,
  senderPlayerId,
  galaxyMembers,
  subscriptionStatus = 'Subscribe',
  setProgress,
  galaxyChatOnlinePlayers,
  isPlayerInRole,
  setMessages,
  messages = [],
  isChatHistoryLoading = false,
  isChatHistoryLoaded = false,
  chatHistoryError = null,
  onRetryChatHistory,
  isGalaxyMember = false,
  onSubscribe,
  onCancelRequest,
  onUnsubscribe,
  membershipButtonLabel,
  className,
  galaxyName = '',
}) => {
  const { player } = useContext(AuthContext);
  const { width } = useContext(ViewPortContext);

  const [messageText, setMessageText] = useState('');
  const from = 'galaxyChat';
  const [voiceMemo, setVoiceMemo] = useState(false);
  const [picker, setPicker] = useState(false);
  const [isTyping, register] = useIsTyping({ timeout: 300 });
  const [isTypingDisplay, SetIsTypingDisplay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendingFile, setSendingFile] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const { status, startRecording, stopRecording, mediaBlobUrl, clearBlobUrl } =
    useReactMediaRecorder({ audio: true });
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [error, setError] = useState('');
  const [messageCount, setMessageCount] = useState(10);
  const [offlineMembers, setOfflineMembers] = useState([]);

  const scrollRef = useRef(null);
  const mobileView = useMobileView();
  const menu = useRef(null);
  const fileInput = useRef(null);
  const [cursorPosition, setCursorPosition] = useState();
  const [file, setFile] = useState(null);
  const textRef = useRef(null);
  const messageRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const chatContainerRef = useRef(null);
  const [isViewMoreClicked, setIsViewMoreClicked] = useState(false);
  const messageSoundRef = useRef();

  // Uploader (singleton via ref)
  const uploaderRef = useRef(null);
  if (!uploaderRef.current) {
    // Extend video mime types to include audio for voice memos
    const customVideoTypes = new Set([
      ...DEFAULT_VIDEO_MIME_TYPES,
      'audio/mpeg',
      'audio/mpeg-3',
      'audio/mp3',
    ]);
    const customIsVideoName = (name = '') =>
      /\.(mp4|m4v|webm|ogg|ogv|mov|flv|3gp|3g2|avi|mkv|mp3)$/i.test(name);

    uploaderRef.current = createMediaUploader({
      maxImageBytes: MAX_IMAGE_SIZE,
      maxVideoBytes: MAX_VIDEO_SIZE,
      videoMimeTypes: customVideoTypes,
      isVideoName: customIsVideoName,
      forceAutoResourceType: true,
      enableAdaptiveLadder: false, // keep lean for chat
      enableGifOptimization: true,
      enableThumbnails: false, // optional for chat – disable to save time
      enableHashDedupe: true,
      debug: false,
      signed: false, // unsigned preset
    });
  }
  const uploader = uploaderRef.current;

  const showMore = () => {
    setMessageCount((prev) => prev + 10);
  };

  const chatMessages = Array.isArray(messages) ? messages : [];
  const canViewGalaxyChat =
    listIncludesPlayer(galaxyMembers, senderId) ||
    subscriptionStatus === 'Subscribed';
  const showHistoryLoading =
    subscriptionStatus === 'Subscribed' && isChatHistoryLoading;
  const showHistoryError =
    subscriptionStatus === 'Subscribed' &&
    Boolean(chatHistoryError) &&
    chatMessages.length === 0;
  const showEmptyHistory =
    subscriptionStatus === 'Subscribed' &&
    isChatHistoryLoaded &&
    !showHistoryLoading &&
    !chatHistoryError &&
    chatMessages.length === 0;

  const handleClickOutside = () => {
    setPicker((prev) => !prev);
  };

  useClickOutside(menu, () => handleClickOutside());

  const [showConfettiText, ConfettiTextComponent] = useConfettiText(
    !mobileView ? (voiceMemo ? messageRef : textRef) : messageRef,
    mobileView,
    '+1 JGK So Fun!',
    MESSAGE_PARTICLE_COUNT,
    'messenger',
    voiceMemo,
  );

  useEffect(() => {
    if (cursorPosition && textRef !== null)
      textRef.current.selectionEnd = cursorPosition;
  }, [cursorPosition]);

  useEffect(() => {
    const newOfflineMembers = galaxyMembers.filter(
      (memberId) =>
        !galaxyChatOnlinePlayers.some(
          (chatPlayer) => chatPlayer.id === memberId,
        ),
    );
    setOfflineMembers(newOfflineMembers);
  }, [galaxyChatOnlinePlayers, galaxyMembers]);

  useEffect(() => {
    if (!messageSoundRef.current) {
      messageSoundRef.current = new Audio(INCOMING_MESSAGE_BL_URL);
    }

    if (!listIncludesPlayer(galaxyMembers, senderId)) {
      return;
    }

    socket.emit('addPlayerToGalaxyChat', { galaxyId, playerId: senderId });

    const handleNewMessage = (data) => {
      if (!listIncludesPlayer(galaxyMembers, data.senderId)) {
        return;
      }
      setMessages((prevMessages) => [
        ...(Array.isArray(prevMessages) ? prevMessages : []),
        data,
      ]);

      if (data.senderId !== senderId) {
        const playPromise = messageSoundRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.log(error);
          });
        }
      }
    };

    socket.on('getGalaxyChatMessage', handleNewMessage);

    return () => {
      socket.emit('removePlayerFromGalaxyChat', {
        galaxyId,
        playerId: senderId,
      });
      socket.off('getGalaxyChatMessage', handleNewMessage);
    };
  }, [galaxyId, senderId, galaxyMembers]);

  useEffect(() => {
    const observer = new MutationObserver((mutationsList) => {
      let isLoadMoreMutation = false;
      for (let mutation of mutationsList) {
        if (mutation.type === 'childList') {
          for (let node of mutation.addedNodes) {
            if (
              node.classList &&
              node.classList.contains('load-more-messages')
            ) {
              isLoadMoreMutation = true;
              setIsViewMoreClicked(true);
              break;
            }
          }
        }
        if (isLoadMoreMutation) break;
      }
      if (!isLoadMoreMutation && !isViewMoreClicked) {
        setTimeout(() => {
          if (chatContainerRef.current) {
            const scrollHeight = chatContainerRef.current.scrollHeight;
            chatContainerRef.current.scrollTop = scrollHeight;
          }
        }, 1000);
      }
    });

    if (chatContainerRef.current) {
      observer.observe(chatContainerRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => observer.disconnect();
  }, [messages, isViewMoreClicked]);

  useEffect(() => {
    if (chatContainerRef.current && !isViewMoreClicked) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages, isViewMoreClicked]);

  useEffect(() => {
    if (file && chatContainerRef.current) {
      const scrollHeight = chatContainerRef.current.scrollHeight;
      chatContainerRef.current.scrollTop = scrollHeight;
    }
  }, [file]);

  const handleMicClick = () => {
    setVoiceMemo(true);
    if (!isRecording) {
      startRecording();
    } else {
      stopRecording();
    }
    setIsRecording((prev) => !prev);
  };

  // ============ NEW: Use mediaUpload utility for audio (voice memo) ============
  const handleSendAudioMemo = async () => {
    if (!listIncludesPlayer(galaxyMembers, senderId)) return;

    if (!mediaBlobUrl) {
      setVoiceMemo(false);
      return;
    }

    let transformedUrl = '';
    try {
      setLoading(true);
      setProgress(0);
      setUploadPercentage(0);

      const audioBlob = await fetch(mediaBlobUrl).then((r) => r.blob());
      const fileName = Date.now() + '.mp3';
      const audioFile = new File([audioBlob], fileName, { type: 'audio/mpeg' });

      // Validate (will throw if unsupported)
      uploader.validate(audioFile);

      const result = await uploader.upload(audioFile, {
        publicIdPrefix: 'GalaxyChat/Audio',
        onProgress: (pct) => {
          setProgress(pct);
          setUploadPercentage(pct);
        },
        transform: true,
      });

      transformedUrl = result.transformedUrl;
      setProgress(100);
      setUploadPercentage(100);
    } catch (e) {
      setLoading(false);
      setMessageText('');
      setError(e.message || 'Audio upload failed.');
      setVoiceMemo(false);
      clearBlobUrl();
      return;
    }

    const message = {
      galaxyId,
      senderId: player.data.player._id,
      text: messageText,
      img: transformedUrl,
      senderProfilePhoto: player.data.player.profilePhoto,
      senderPlayerId: player.data.player.playerId,
      createdAt: new Date(),
      tempId: uuidv4(),
    };

    socket.emit('sendGalaxyChatMessage', message);

    try {
      await axiosConn.post(
        'galaxyMessages',
        { message, offlineMembers },
        {
          headers: { Authorization: `Bearer ${player.token}` },
        },
      );
      setMessageText('');
      setFile(null);
      if (fileInput.current) fileInput.current.value = null;
      showConfettiText();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setMessageText('');
      setError(err.message);
    }

    setVoiceMemo(false);
    clearBlobUrl();
  };

  // ============ NEW: Use mediaUpload utility for image/video attachments ============
  const handleSubmit = async (e) => {
    if (
      !listIncludesPlayer(galaxyMembers, senderId) &&
      subscriptionStatus !== 'Subscribed'
    ) {
      return;
    }

    if (
      (e.key === 'Enter' || e.type === 'click') &&
      (messageText.trim() !== '' || file) // CHANGED: allow send when file state present
    ) {
      let transformedUrl = '';

      if (file) {
        try {
          setLoading(true);
          setSendingFile(true);
          setProgress(0);
          setUploadPercentage(0);

          // Validate via uploader (size/type gates)
          uploader.validate(file);

          const { transformedUrl: tUrl } = await uploader.upload(file, {
            publicIdPrefix: 'GalaxyChat/Media',
            onProgress: (pct) => {
              setProgress(pct);
              setUploadPercentage(pct);
            },
            transform: true,
          });

          transformedUrl = tUrl;
        } catch (e) {
          setLoading(false);
          setSendingFile(false);
          if (e instanceof MediaUploadError) {
            setError(e.message);
          } else {
            setError(e.message || 'Upload failed.');
          }
          return;
        }
      }

      const message = {
        galaxyId,
        senderId: player.data.player._id,
        text: messageText.trim(),
        img: transformedUrl,
        senderProfilePhoto: player.data.player.profilePhoto,
        senderPlayerId: player.data.player.playerId,
        createdAt: new Date(),
        tempId: uuidv4(),
      };

      try {
        socket.emit('sendGalaxyChatMessage', message);
      } catch (err) {
        setError(err.message);
        setLoading(false);
        setSendingFile(false);
        return;
      }

      try {
        await axiosConn.post(
          'galaxyMessages',
          { message, offlineMembers },
          {
            headers: { Authorization: `Bearer ${player.token}` },
          },
        );

        setLoading(false);
        setMessageText('');
        setFile(null);
        if (fileInput.current) fileInput.current.value = '';
        setSendingFile(false);
        setProgress(100);
        setUploadPercentage(100);
        showConfettiText();
      } catch (err) {
        setLoading(false);
        setMessageText('');
        setError(err.message);
        setSendingFile(false);
      }
    }
  };

  const debouncedHandleSubmit = debounce(handleSubmit, 700);

  const handleDeleteMessage = (messageId) => {
    setMessages(
      chatMessages.filter(
        (m) => m._id !== messageId && m.tempId !== messageId,
      ),
    );
  };

  return (
    <>
      {(subscriptionStatus === 'Subscribe' ||
        subscriptionStatus === 'Requested') && (
        <>
          <div className="centered-column-galaxyChatBox">
            <WelcomeMessage
              subscriptionStatus={subscriptionStatus}
              playerId={player.data.player.playerId}
              isPlayerInRole={isPlayerInRole}
              onSubscribe={onSubscribe}
              onCancelRequest={onCancelRequest}
              onUnsubscribe={onUnsubscribe}
              membershipButtonLabel={membershipButtonLabel}
              className={className}
              galaxyName={galaxyName}
            />
            {subscriptionStatus === 'Subscribed' && <AdRotator />}
          </div>
        </>
      )}
      <div className="chat-container" ref={chatContainerRef}>
        {subscriptionStatus === 'Subscribed' &&
          messageCount < chatMessages.length && (
            <div
              className={
                !mobileView
                  ? 'viewMessages load-more-messages'
                  : 'viewMessagesMobile load-more-messages'
              }
              onClick={() => showMore()}
            >
              View {chatMessages.length - messageCount} more messages
            </div>
          )}
        {showHistoryLoading && (
          <div className="galaxy-chat-state" role="status">
            <p className="galaxy-chat-state__title">Loading chat history...</p>
          </div>
        )}
        {showHistoryError && (
          <div className="galaxy-chat-state galaxy-chat-state--error">
            <p className="galaxy-chat-state__title">
              Could not load chat history.
            </p>
            <p className="galaxy-chat-state__body">
              Your composer still works, but the previous messages need a
              refresh.
            </p>
            {typeof onRetryChatHistory === 'function' && (
              <button
                type="button"
                className="galaxy-chat-state__retry"
                onClick={onRetryChatHistory}
              >
                Retry
              </button>
            )}
          </div>
        )}
        {showEmptyHistory && (
          <div className="galaxy-chat-state">
            <p className="galaxy-chat-state__title">
              No Galaxy messages yet.
            </p>
            <p className="galaxy-chat-state__body">
              Start the first conversation for this Galaxy.
            </p>
          </div>
        )}
        {!showHistoryLoading &&
          !showHistoryError &&
          chatMessages
            .slice(Math.max(chatMessages.length - messageCount, 0))
            .map((message) => {
              const isOwnMessage = message.senderId === senderId;
              return (
                canViewGalaxyChat && (
                  <div
                    key={message._id}
                    className={isOwnMessage ? 'message-own' : 'message-other'}
                  >
                    <Message
                      message={message}
                      own={isOwnMessage}
                      senderProfilePhoto={message.senderProfilePhoto}
                      from="galaxyChat"
                      onDeleteMessage={handleDeleteMessage}
                      galaxyId={galaxyId}
                      isPlayerInRole={isPlayerInRole}
                    />
                  </div>
                )
              );
            })}
        <div className="bottomMessages" ref={scrollRef} />
        <div className="chat-input">
          <ConfettiTextComponent />
        </div>
        <ChatBoxMiddle
          isTypingDisplay={isTypingDisplay}
          senderPlayerId={senderPlayerId}
          status={status}
          mediaBlobUrl={mediaBlobUrl}
          loading={loading}
          handleMicClick={handleMicClick}
          isRecording={isRecording}
          mobileView={mobileView}
          width={width}
          setVoiceMemo={setVoiceMemo}
          clearBlobUrl={clearBlobUrl}
          handleSendAudioMemo={handleSendAudioMemo}
          fileInput={fileInput}
          handleFileInput={(e) =>
            handleFileInput(e, setError, setFile, maxVideoSize, maxImageSize)
          }
          error={error}
          setError={setError}
          setFile={setFile}
          setText={setMessageText}
          file={file}
          setLoading={setLoading}
          setIsLoading={setIsLoading}
          setSendingFile={setSendingFile}
          messageRef={messageRef}
          chatContainerRef={chatContainerRef}
          handleSubmit={handleSubmit} // NEW: pass for preview Send button & fallback
        />
      </div>

      <div className="chatBoxBottomContainer">
        {!voiceMemo && isGalaxyMember && (
          <ChatBoxBottom
            voiceMemo={voiceMemo}
            picker={picker}
            menu={menu}
            handleEmoji={(e, { emoji }) =>
              handleEmoji(
                e,
                { emoji },
                messageText,
                setMessageText,
                textRef,
                setCursorPosition,
              )
            }
            textRef={textRef}
            register={register}
            text={messageText}
            setText={setMessageText}
            handleSubmit={debouncedHandleSubmit}
            loading={loading}
            sendingFile={sendingFile}
            handleMicClick={handleMicClick}
            isRecording={isRecording}
            setPicker={setPicker}
            fileInput={fileInput}
            setSendingFile={setSendingFile}
            handleSendAudioMemo={handleSendAudioMemo}
            parent="GalaxyChatBox"
          />
        )}
      </div>
    </>
  );
};

export default GalaxyChatBox;
